import { useState, useEffect, useRef } from 'react'
import { showsTrialUx } from '../utils/offerFlags'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { AvisDeMerlinBlock, BioAnalysisBlock, AudienceSignalsBlock, Stars as MerlinStars, Dropdown as MerlinDropdown, MiniDropdown as MerlinMiniDropdown } from '../components/MerlinAnalysisBlocks'
import { useAuth } from '../context/AuthContext'
import { AccountScore } from './StudioPage'
import { sb, API_URL } from '../utils/config'
import { studioEvents } from '../utils/metaPixel'
import merlinSort from '../assets/Merlin_sort-removebg-preview.png'
import merlinSucces from '../assets/Merlin_succes-removebg-preview.png'
import { ANTONIN_VIDEO_ANALYSIS } from './landingFixtures'
import merlinIdee from '../assets/Merlin_idée_1-removebg-preview.png'
import merlinRecherche from '../assets/Merlin_recherche_boule_de_cristal-removebg-preview.png'

const SEGMENT_LABELS = {
  hook: 'Hook',
  structure: 'Structure',
  loop: 'Boucle',
  cta: 'CTA',
  density: 'Densité',
}

const SEGMENT_DESCRIPTIONS = {
  hook: 'Les 3 premières secondes',
  structure: 'Arc narratif',
  loop: 'Re-watch potential',
  cta: 'Appel à l\'action',
  density: 'Info / seconde',
}

const VIDEO_ANALYSIS_STEPS = [
  { pct: 5,   label: 'Chargement de ta vidéo' },
  { pct: 15,  label: 'Téléchargement' },
  { pct: 30,  label: 'Visionnage' },
  { pct: 45,  label: 'Transcription' },
  { pct: 60,  label: 'Description du visuel' },
  { pct: 70,  label: 'Lecture des commentaires' },
  { pct: 80,  label: 'Analyse en profondeur' },
  { pct: 95,  label: 'Préparation du rapport' },
]

const ACCOUNT_ANALYSIS_STEPS = [
  { pct: 10, label: 'Découverte de ton compte' },
  { pct: 18, label: 'Lecture de ta bio' },
  { pct: 35, label: 'Étude de tes meilleures vidéos' },
  { pct: 50, label: 'Compréhension de ton style' },
  { pct: 68, label: 'Écoute de ton audience' },
  { pct: 78, label: 'Recherche de tes inspirations' },
  { pct: 86, label: 'Avis de Merlin' },
  { pct: 92, label: 'Analyse détaillée de ta bio' },
  { pct: 96, label: 'Préparation de tes conseils' },
  { pct: 98, label: 'Finalisation' },
]

// Cache module-level pour les reels et les analyses : évite le re-fetch à
// chaque retour sur l'onglet (TTL 30 min, ou explicite via "Rafraîchir").
const _cache = {
  reels: { data: null, fetchedAt: 0 },
  videoAnalyses: { data: null, fetchedAt: 0 },
  accountAnalyses: { data: null, fetchedAt: 0 },
}
const CACHE_TTL = 30 * 60 * 1000

function getCached(key) {
  const entry = _cache[key]
  if (!entry || !entry.data) return null
  if (Date.now() - entry.fetchedAt > CACHE_TTL) return null
  return entry.data
}
function setCached(key, data) {
  _cache[key] = { data, fetchedAt: Date.now() }
}
export function invalidateAnalysisCache(key) {
  if (key) _cache[key] = { data: null, fetchedAt: 0 }
  else for (const k of Object.keys(_cache)) _cache[k] = { data: null, fetchedAt: 0 }
}

export default function StudioAnalysisTab() {
  const [section, setSection] = useState('videos')
  const auth = useAuth()
  // L'admin bypasse la gate Pro+ (comme le backend, qui autorise l'admin par
  // email) -> sinon l'analyse compte "disparaît" derrière le paywall en test.
  const isProPlus = auth.studioPlan === 'pro_plus' || auth.isAdmin

  // Deep-link sous-onglet : le bouton "Comment améliorer mon compte" (radar)
  // ouvre l'onglet Analyse ET bascule directement sur "Mon compte".
  useEffect(() => {
    function handler() { setSection('account') }
    window.addEventListener('studio:goto-account-analysis', handler)
    return () => window.removeEventListener('studio:goto-account-analysis', handler)
  }, [])

  return (
    <div className="space-y-5">
      <div className="flex gap-1 bg-ink/5 rounded-lg p-1">
        <button onClick={() => setSection('videos')}
          className={`flex-1 text-xs sm:text-sm font-bold py-2 rounded-md transition-colors flex items-center justify-center gap-1.5 ${section === 'videos' ? 'bg-white shadow-sm text-ink' : 'text-ink-muted'}`}>
          🎬 <span>Mes vidéos</span>
        </button>
        <button onClick={() => setSection('account')}
          className={`flex-1 text-xs sm:text-sm font-bold py-2 rounded-md transition-colors flex items-center justify-center gap-1.5 ${section === 'account' ? 'bg-white shadow-sm text-ink' : 'text-ink-muted'} relative`}>
          🎯 <span>Mon compte</span>
          {!isProPlus && (
            <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ml-1"
              style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', color: 'white' }}>
              Pro
            </span>
          )}
        </button>
      </div>

      {/* display:none au lieu de unmount : les analyses en cours continuent
          à poller, le state local est préservé quand on switch de section. */}
      <div style={{ display: section === 'videos' ? 'block' : 'none' }}>
        <VideosSection />
      </div>
      <div style={{ display: section === 'account' ? 'block' : 'none' }}>
        <AccountSection isProPlus={isProPlus} active={section === 'account'} />
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════

async function authHeaders() {
  const { data } = await sb.auth.getSession()
  const token = data?.session?.access_token
  return token ? { Authorization: `Bearer ${token}` } : {}
}

// ═══════════════════════════════════════════════════════════════
// SECTION VIDÉOS — grid des reels + analyse 1-vidéo
// ═══════════════════════════════════════════════════════════════
function VideosSection() {
  const auth = useAuth()
  const [reels, setReels] = useState(null)
  const [loadingReels, setLoadingReels] = useState(true)
  const [errorReels, setErrorReels] = useState(null)
  const [igUsername, setIgUsername] = useState(null)
  const [pastAnalyses, setPastAnalyses] = useState([])
  const [quota, setQuota] = useState(null)
  const [plan, setPlan] = useState(null)
  const [activeAnalysisId, setActiveAnalysisId] = useState(null)
  const [confirmReel, setConfirmReel] = useState(null)

  // Gate : seuls Créateur payant + Pro+ peuvent lancer une analyse.
  // Free + Créateur en essai → click ouvre paywall direct.
  const inTrial = (() => {
    try { return typeof window !== 'undefined' && localStorage.getItem('studio_trial_dev') === '1' } catch { return false }
  })()
  const currentPlan = auth.studioPlan
  const canLaunchAnalysis =
    currentPlan === 'pro_plus' || (currentPlan === 'pro' && !inTrial)

  // Map shortcode → analysis (la plus récente, peu importe le status — on inclut
  // pending/analyzing pour que l'user puisse cliquer dessus et voir le progress).
  const analysesByShortcode = {}
  for (const a of pastAnalyses) {
    if (!analysesByShortcode[a.reel_shortcode]) analysesByShortcode[a.reel_shortcode] = a
  }

  // Analyses en cours (pending/analyzing) — affichées en haut pour reprendre
  const inProgressAnalyses = pastAnalyses.filter(a => a.status === 'pending' || a.status === 'analyzing')
  const doneAnalyses = pastAnalyses.filter(a => a.status === 'done')

  async function loadReels(force = false) {
    if (!force) {
      const cached = getCached('reels')
      if (cached) {
        setReels(cached.reels)
        setIgUsername(cached.ig_username)
        setLoadingReels(false)
        return
      }
    }
    setLoadingReels(true)
    setErrorReels(null)
    try {
      const h = await authHeaders()
      const r = await fetch(`${API_URL}/api/studio/my-reels${force ? '?refresh=1' : ''}`, { headers: h })
      const d = await r.json()
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`)
      const payload = { reels: d.reels || [], ig_username: d.ig_username || null }
      setCached('reels', payload)
      setReels(payload.reels)
      setIgUsername(payload.ig_username)
    } catch (e) {
      setErrorReels(e.message)
    } finally { setLoadingReels(false) }
  }

  async function loadAnalysesList(force = false) {
    if (!force) {
      const cached = getCached('videoAnalyses')
      if (cached) {
        setPastAnalyses(cached.analyses)
        setQuota(cached.quota)
        setPlan(cached.plan)
        return
      }
    }
    try {
      const h = await authHeaders()
      const r = await fetch(`${API_URL}/api/studio/video-analysis`, { headers: h })
      const d = await r.json()
      if (r.ok) {
        const payload = { analyses: d.analyses || [], quota: d.quota || null, plan: d.plan || null }
        setCached('videoAnalyses', payload)
        setPastAnalyses(payload.analyses)
        setQuota(payload.quota)
        setPlan(payload.plan)
      }
    } catch { /* silent */ }
  }

  useEffect(() => { loadReels(); loadAnalysesList() }, [])

  // Arrivée depuis le mail « analyse vidéo offerte » (?va=1) : ouvre
  // directement le rapport le plus récent au lieu de laisser l'analyse
  // dans la liste (un clic de moins, zéro confusion sur où regarder).
  const vaOpenedRef = useRef(false)
  useEffect(() => {
    if (vaOpenedRef.current) return
    if (new URLSearchParams(window.location.search).get('va') !== '1') return
    const done = (pastAnalyses || []).filter(a => a.status === 'done')
    if (done.length === 0) return
    vaOpenedRef.current = true
    setActiveAnalysisId(done[0].id)
  }, [pastAnalyses])

  // Listen 'studio:open-video-analysis' (relauncher → ouvre la nouvelle analyse)
  useEffect(() => {
    function handler(e) {
      const id = e.detail?.id
      if (id) setActiveAnalysisId(id)
    }
    window.addEventListener('studio:open-video-analysis', handler)
    return () => window.removeEventListener('studio:open-video-analysis', handler)
  }, [])

  // Tant qu'il y a au moins 1 analyse en cours, on refresh la liste toutes les
  // 5s pour suivre la progression sans que l'user ait besoin d'être dessus.
  useEffect(() => {
    if (inProgressAnalyses.length === 0) return
    const intv = setInterval(() => loadAnalysesList(true), 5000)
    return () => clearInterval(intv)
  }, [inProgressAnalyses.length])

  function onReelClick(reel) {
    // Si analyse existante (done ou en cours) → l'ouvrir direct
    const existing = analysesByShortcode[reel.shortcode]
    if (existing && existing.status !== 'error') { setActiveAnalysisId(existing.id); return }

    // Gate plan : free ou Créateur en essai → paywall direct, pas de launch
    if (!canLaunchAnalysis) {
      const isCreatorTrial = currentPlan === 'pro' && inTrial
      window.dispatchEvent(new CustomEvent('studio:open-analysis-paywall', {
        detail: {
          // Si Créateur en essai → pousse Pro+ OU offre option "résilier essai"
          // Si Free → pousse Créateur trial
          upgrade_to: isCreatorTrial ? 'pro_plus' : 'studio_creator',
          from_free: !isCreatorTrial,
          code: isCreatorTrial ? 'trial_blocks_analysis' : 'free_no_analysis',
        },
      }))
      return
    }

    // Sinon (rien, ou erreur précédente) → popup de confirmation pour relancer
    setConfirmReel(reel)
  }

  async function launchAnalysis(reel) {
    setConfirmReel(null)
    try {
      const h = await authHeaders()
      const r = await fetch(`${API_URL}/api/studio/video-analysis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...h },
        body: JSON.stringify({ shortcode: reel.shortcode }),
      })
      const d = await r.json()
      if (!r.ok) {
        if (d.code === 'lifetime_quota_reached' || d.code === 'weekly_quota_reached') {
          window.dispatchEvent(new CustomEvent('studio:open-analysis-paywall', {
            detail: { quota: d.quota, upgrade_to: d.upgrade_to, code: d.code },
          }))
          return
        }
        throw new Error(d.error || `HTTP ${r.status}`)
      }
      invalidateAnalysisCache('videoAnalyses')
      // Activation : une analyse VIDEO a demarre → coche l'etape 'video'.
      try { studioEvents.videoAnalysisStarted({ analysis_id: d.id }) } catch {}
      try { window.dispatchEvent(new CustomEvent('studio:video-analysis-started')) } catch {}
      setActiveAnalysisId(d.id)
    } catch (e) {
      alert(`Erreur : ${e.message}`)
    }
  }

  return (
    <div className="space-y-4">
      {/* Modal popup viewer en overlay au lieu de remplacer la page */}
      {activeAnalysisId && (
        <VideoAnalysisModal
          analysisId={activeAnalysisId}
          plan={plan}
          onClose={() => { setActiveAnalysisId(null); loadAnalysesList() }}
        />
      )}
      <QuotaBar quota={quota} plan={plan} />

      {/* En cours d'analyse — strip prominent pour montrer que ça tourne */}
      {inProgressAnalyses.length > 0 && (
        <InProgressStrip
          analyses={inProgressAnalyses}
          onOpen={(a) => setActiveAnalysisId(a.id)}
        />
      )}

      {/* Past analyses prominents */}
      {doneAnalyses.length > 0 && (
        <PastAnalysesStrip
          analyses={doneAnalyses.slice(0, 4)}
          onOpen={(a) => setActiveAnalysisId(a.id)}
        />
      )}

      {/* Grid reels à analyser */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-[10px] uppercase tracking-wider font-bold text-ink-muted">
            {igUsername ? `Tes reels (@${igUsername})` : 'Tes reels'}
          </p>
          <button onClick={() => loadReels(true)}
            className="text-[10px] text-ink-muted hover:text-ink transition-colors underline underline-offset-2">
            Rafraîchir
          </button>
        </div>
        {loadingReels && <LoadingState message="Récupération de tes reels…" />}
        {errorReels && (
          <div className="p-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm">
            <p className="font-bold">{errorReels}</p>
            {errorReels.includes('No ig_username') && (
              <p className="mt-2 text-xs">Fais un scan Studio (onglet "Pour toi") d'abord, puis reviens ici.</p>
            )}
            {errorReels === 'Not found' && (
              <p className="mt-2 text-xs">Le backend redéploie. Réessaie dans 1 min.</p>
            )}
          </div>
        )}
        {reels && reels.length > 0 && (
          <ReelsGrid reels={reels} analysesByShortcode={analysesByShortcode} onSelect={onReelClick} />
        )}
        {reels && reels.length === 0 && (
          <div className="text-center py-10 text-sm text-ink-muted">
            Aucun reel détecté sur ton compte.
          </div>
        )}
      </div>

      {/* Modal de confirmation */}
      {confirmReel && (
        <VideoAnalysisConfirmModal
          reel={confirmReel}
          quota={quota}
          plan={plan}
          onCancel={() => setConfirmReel(null)}
          onConfirm={() => launchAnalysis(confirmReel)}
        />
      )}
    </div>
  )
}

function QuotaBar({ quota, plan }) {
  if (!quota) return null
  const isUnlimited = quota.period === 'unlimited'
  const isPro = plan === 'pro_plus'
  const planLabel = isPro ? 'Pro' : plan === 'pro' ? 'Créateur' : 'Free'
  return (
    <div className="flex items-center justify-between p-3 rounded-xl"
      style={{
        background: isPro ? 'linear-gradient(135deg, rgba(var(--merlin-accent-rgb, 254,130,4),0.10), rgba(var(--merlin-violet-rgb, 124,58,237),0.05))' : 'rgba(15,16,64,0.04)',
        border: `1px solid ${isPro ? 'rgba(var(--merlin-accent-rgb, 254,130,4),0.25)' : 'rgba(15,16,64,0.10)'}`,
      }}>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wider font-bold text-ink-muted">Plan</p>
        <p className="text-sm font-bold text-ink leading-snug">
          Studio {planLabel}
          {isPro && <span className="ml-1.5 text-[10px] uppercase tracking-wider text-orange">Illimité</span>}
        </p>
      </div>
      {!isUnlimited && (
        <div className="text-right shrink-0 ml-3">
          <p className="text-[10px] uppercase tracking-wider font-bold text-ink-muted">Analyses</p>
          <p className="text-sm font-bold text-ink">
            <span className={quota.remaining > 0 ? 'text-green-600' : 'text-rose-600'}>{quota.remaining}</span>
            <span className="text-ink-muted">/{quota.limit}</span>
            <span className="text-[10px] text-ink-muted ml-1">{quota.period === 'week' ? '/sem' : 'à vie'}</span>
          </p>
        </div>
      )}
    </div>
  )
}

function InProgressStrip({ analyses, onOpen }) {
  return (
    <div className="p-3 rounded-xl"
      style={{ background: 'linear-gradient(135deg, rgba(var(--merlin-accent-rgb, 254,130,4),0.08), rgba(245,158,11,0.04))', border: '1px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.25)' }}>
      <div className="flex items-center gap-2 mb-2">
        <span className="w-3 h-3 border-2 border-orange/40 border-t-orange rounded-full animate-spin" />
        <p className="text-[10px] uppercase tracking-wider font-bold text-orange-700">
          {analyses.length} analyse{analyses.length > 1 ? 's' : ''} en cours
        </p>
      </div>
      <div className="space-y-1.5">
        {analyses.map(a => (
          <button key={a.id} onClick={() => onOpen(a)}
            className="w-full text-left p-2.5 rounded-lg bg-white border border-border hover:border-orange/40 transition-colors flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-ink truncate">
                {a.reel_metadata?.caption?.slice(0, 60) || `Vidéo ${a.reel_shortcode}`}
              </p>
              <p className="text-[10px] text-ink-muted mt-0.5 truncate">{a.current_step || 'En attente…'}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-sm font-bold text-orange">{a.progress_pct || 0}%</p>
              <p className="text-[9px] text-ink-faint">Voir →</p>
            </div>
          </button>
        ))}
      </div>
    </div>
  )
}

function PastAnalysesStrip({ analyses, onOpen }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider font-bold text-ink-muted mb-2">Tes analyses récentes</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {analyses.map(a => (
          <button key={a.id} onClick={() => onOpen(a)}
            className="text-left p-2.5 rounded-lg border border-border bg-white hover:border-orange/40 hover:shadow-md transition-all">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-[9px] uppercase tracking-wider font-bold text-green-700">✓ Analysée</span>
              {a.scoring?.global_score != null && (
                <span className="ml-auto text-xs font-bold text-orange">{a.scoring.global_score.toFixed(1)}/10</span>
              )}
            </div>
            <p className="text-[11px] text-ink font-bold truncate">{a.reel_metadata?.caption?.slice(0, 60) || a.reel_shortcode}</p>
            <p className="text-[10px] text-ink-muted mt-0.5">
              {a.reel_metadata?.views ? (a.reel_metadata.views / 1000).toFixed(1) + 'K vues' : ''}
            </p>
          </button>
        ))}
      </div>
    </div>
  )
}

function ReelsGrid({ reels, analysesByShortcode, onSelect }) {
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
      {reels.map(reel => {
        const existing = analysesByShortcode[reel.shortcode]
        const isDone = existing?.status === 'done'
        const isInProgress = existing?.status === 'pending' || existing?.status === 'analyzing'
        const isError = existing?.status === 'error'
        return (
          <button key={reel.shortcode} onClick={() => onSelect(reel)}
            className="group relative aspect-[9/16] rounded-lg overflow-hidden bg-ink/10 hover:scale-[1.02] transition-transform">
            {reel.thumbnail_url ? (
              <img src={reel.thumbnail_url} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-ink-muted text-xs">No thumb</div>
            )}
            {/* Badge status */}
            {isDone && (
              <div className="absolute top-1.5 left-1.5 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #10b981, #059669)', boxShadow: '0 2px 6px rgba(0,0,0,0.3)' }}>
                ✓
              </div>
            )}
            {isInProgress && (
              <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold text-white flex items-center gap-1"
                style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.95)', boxShadow: '0 2px 6px rgba(0,0,0,0.3)' }}>
                <span className="w-1.5 h-1.5 border border-white/40 border-t-white rounded-full animate-spin" />
                {existing.progress_pct || 0}%
              </div>
            )}
            {isError && (
              <div className="absolute top-1.5 left-1.5 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                style={{ background: '#dc2626', boxShadow: '0 2px 6px rgba(0,0,0,0.3)' }}>
                !
              </div>
            )}
            {/* Métriques */}
            <div className="absolute top-1.5 right-1.5 text-[8px] font-bold text-white px-1.5 py-0.5 rounded"
              style={{ background: 'rgba(15,10,26,0.65)' }}>
              {(reel.views / 1000).toFixed(0)}K
            </div>
            {/* Hover overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent flex flex-col justify-end p-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <p className="text-[9px] text-white/90 font-bold">
                {isDone ? 'Voir l\'analyse →' : isInProgress ? 'En cours →' : isError ? 'Erreur, réessayer' : 'Analyser →'}
              </p>
              <p className="text-[8px] text-white/60">{reel.duration_seconds}s</p>
            </div>
          </button>
        )
      })}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// MODAL DE CONFIRMATION — avant le launch
// ═══════════════════════════════════════════════════════════════
function VideoAnalysisConfirmModal({ reel, quota, plan, onCancel, onConfirm }) {
  const isPro = plan === 'pro_plus'
  const isLastFree = !isPro && quota && quota.remaining === 1
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onCancel}
      style={{ background: 'rgba(15,10,26,0.7)', backdropFilter: 'blur(6px)' }}>
      <div className="w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        {/* Header thumb + caption */}
        <div className="flex items-start gap-3 p-4 border-b border-border">
          {reel.thumbnail_url && (
            <img src={reel.thumbnail_url} alt=""
              className="w-16 h-20 object-cover rounded-md shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-wider font-bold text-ink-muted mb-1">Ta vidéo</p>
            <p className="text-sm font-bold text-ink leading-snug line-clamp-2 mb-1">
              {reel.caption?.slice(0, 100) || `Vidéo ${reel.shortcode}`}
            </p>
            <p className="text-[11px] text-ink-faint">
              {(reel.views / 1000).toFixed(1)}K vues, {reel.likes != null ? `${reel.likes.toLocaleString('fr-FR')} likes` : 'likes masqués'}, {reel.duration_seconds}s
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-5">
          <div className="text-center mb-4">
            <img src={merlinIdee} alt="" className="w-16 h-16 mx-auto object-contain mb-2"
              style={{ filter: 'drop-shadow(0 4px 12px rgba(var(--merlin-accent-rgb, 254,130,4),0.25))' }} />
            <h2 className="text-xl font-bold text-ink leading-tight mb-1">On y va ?</h2>
          </div>

          <div className="space-y-2 mb-5 p-3 rounded-xl" style={{ background: '#fffbf5', border: '1px solid #f1e9dc' }}>
            <div className="flex items-start gap-2 text-xs text-ink leading-relaxed">
              <span className="text-orange shrink-0">✓</span>
              <span>Tu sauras pourquoi cette vidéo a marché (ou pas)</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-ink leading-relaxed">
              <span className="text-orange shrink-0">✓</span>
              <span>Tu verras les leviers psychologiques qu'elle active</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-ink leading-relaxed">
              <span className="text-orange shrink-0">✓</span>
              <span>Tu auras 3 actions concrètes à tester sur ta prochaine</span>
            </div>
            <div className="flex items-center justify-between text-[11px] pt-2 mt-1 border-t border-[#f1e9dc]">
              <span className="text-ink-muted">⏱ 3 à 5 minutes</span>
              {!isPro && quota && (
                <span className={`font-bold ${isLastFree ? 'text-orange' : 'text-ink-muted'}`}>
                  {isLastFree ? 'Ta dernière analyse' : `${quota.remaining}/${quota.limit} restantes`}
                </span>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <button onClick={onCancel}
              className="flex-1 py-3 rounded-xl font-bold text-sm text-ink-muted bg-ink/5 hover:bg-ink/10 transition-colors">
              Pas maintenant
            </button>
            <button onClick={onConfirm}
              className="flex-1 py-3 rounded-xl font-bold text-sm text-white transition-all hover:scale-[1.02] flex items-center justify-center gap-1.5"
              style={{
                background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))',
                boxShadow: '0 6px 20px rgba(var(--merlin-accent-rgb, 254,130,4),0.35)',
              }}>
              <span>C'est parti →</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// VIDEO ANALYSIS MODAL — popup overlay (style Merlin admin)
// ═══════════════════════════════════════════════════════════════
function VideoAnalysisModal({ analysisId, plan, onClose }) {
  // Bloque le scroll body sous le modal
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-0 sm:p-4"
      onClick={onClose}
      style={{ background: 'rgba(15,10,26,0.7)', backdropFilter: 'blur(6px)' }}>
      <div className="w-full sm:max-w-3xl bg-white sm:rounded-2xl shadow-2xl h-full sm:max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        {/* Header sticky avec close */}
        <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-border bg-white shrink-0">
          <p className="text-sm font-bold text-ink">Analyse Merlin</p>
          <button onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center text-ink-muted hover:text-ink hover:bg-ink/5">
            ×
          </button>
        </div>
        {/* Contenu scrollable */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-5">
          <VideoAnalysisViewer analysisId={analysisId} plan={plan} onClose={onClose} hideHeader />
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// VIDEO ANALYSIS VIEWER — polling + dashboard scores
// ═══════════════════════════════════════════════════════════════
function VideoAnalysisViewer({ analysisId, plan, onClose, hideHeader = false, hideUpgrade = false, afterNoteSlot = null }) {
  const [data, setData] = useState(null)
  const pollRef = useRef(null)

  async function poll() {
    try {
      const h = await authHeaders()
      const r = await fetch(`${API_URL}/api/studio/video-analysis/${analysisId}`, { headers: h })
      const d = await r.json()
      if (r.ok) setData(d)
      if (d.status === 'done' || d.status === 'error') {
        if (pollRef.current) clearInterval(pollRef.current)
      }
    } catch { /* silent */ }
  }

  useEffect(() => {
    poll()
    pollRef.current = setInterval(poll, 2500)
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [analysisId])

  if (!data) return <LoadingState message="Chargement de l'analyse…" />

  if (data.status === 'error') {
    return (
      <div className="space-y-3">
        {!hideHeader && (
          <button onClick={onClose} className="text-xs text-ink-muted hover:text-ink underline">← Retour</button>
        )}
        <div className="p-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm">
          <p className="font-bold">L'analyse a échoué.</p>
          <p className="text-xs mt-1">{data.error}</p>
        </div>
      </div>
    )
  }

  if (data.status !== 'done') {
    return <AnalysisProgress data={data} steps={VIDEO_ANALYSIS_STEPS} onCancel={onClose} hideCancel={hideHeader} />
  }

  async function relaunch() {
    try {
      const h = await authHeaders()
      const r = await fetch(`${API_URL}/api/studio/video-analysis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...h },
        body: JSON.stringify({ shortcode: data.reel_shortcode }),
      })
      const d = await r.json()
      if (!r.ok) {
        if (d.code === 'lifetime_quota_reached' || d.code === 'weekly_quota_reached') {
          window.dispatchEvent(new CustomEvent('studio:open-analysis-paywall', {
            detail: { quota: d.quota, upgrade_to: d.upgrade_to, code: d.code },
          }))
          return
        }
        throw new Error(d.error || `HTTP ${r.status}`)
      }
      invalidateAnalysisCache('videoAnalyses')
      // Bascule sur la nouvelle analyse
      onClose?.()
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('studio:open-video-analysis', { detail: { id: d.id } }))
      }, 100)
    } catch (e) {
      alert(`Erreur : ${e.message}`)
    }
  }

  return <VideoAnalysisResult data={data} plan={plan} onClose={onClose} hideHeader={hideHeader} onRelaunch={relaunch} hideUpgrade={hideUpgrade} afterNoteSlot={afterNoteSlot} />
}

// ═══════════════════════════════════════════════════════════════
// FICHE-CADEAU (spec mail-analyse-curiosity §4bis, 24/07) — atterrissage
// du mail « analyse offerte » pour les free/anciens abonnés : l'analyse
// COMPLÈTE déverrouillée (le cadeau est entier, reward_full posé par le
// circuit va-outreach). La monétisation vit sur l'action SUIVANTE : le
// CTA « Analyser une autre de mes vidéos » ouvre le paywall Pro+ v8
// (source va_fiche) — le paywall répond à une demande, pas une interruption.
// ═══════════════════════════════════════════════════════════════
// ── BANDEAU FONDATEURS (campagne 05/08) : visible sur la fiche-cadeau quand
// le drapeau membre est posé (lien mail ?promo=membre, persisté par main.jsx).
// Le CTA ouvre le paywall analyse, qui affiche déjà MemberCardBody (-45% à
// vie) pour les visiteurs membres : aucun tarif dupliqué ici, juste l'accroche.
export function FondateurBanner({ onCta }) {
  useEffect(() => {
    try { studioEvents.tabClicked?.({ tab: 'fondateur_banner_shown', source_page: 'va_fiche' }) } catch { /* no-op */ }
  }, [])
  return (
    <div className="rounded-2xl p-4 mb-3 text-white relative overflow-hidden"
      style={{ background: 'linear-gradient(120deg, #2a1657 0%, #4c1d95 55%, #6d28d9 100%)', boxShadow: '0 10px 26px rgba(76,29,149,0.35)' }}>
      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full" style={{ background: 'rgba(254,130,4,0.25)', filter: 'blur(30px)' }} />
      <p className="text-[10.5px] font-extrabold uppercase tracking-widest" style={{ color: '#fdba74' }}>🏆 Offre fondateurs</p>
      <p className="text-[15px] font-extrabold leading-snug mt-1">Réservée aux 500 premiers comptes Merlin. Le tien en fait partie.</p>
      <p className="text-[12.5px] mt-1" style={{ color: 'rgba(255,255,255,0.85)' }}>
        Merlin à -45 % à vie : 29,90 €/mois en annuel, ou 49,90 €/mois au lieu de 89 €. Valable jusqu'au 6 août minuit.
      </p>
      <button type="button"
        onClick={() => {
          try { studioEvents.tabClicked?.({ tab: 'fondateur_banner_clicked', source_page: 'va_fiche' }) } catch { /* no-op */ }
          onCta?.()
        }}
        className="w-full mt-3 py-3 rounded-xl font-extrabold text-[14px] text-white"
        style={{ background: 'linear-gradient(135deg, #fe8204, #f59e0b)', boxShadow: '0 6px 18px rgba(254,130,4,0.4)' }}>
        J'active mon tarif fondateur
      </button>
    </div>
  )
}

// Fin de l'offre fondateurs : 6 août minuit (nuit du 6 au 7, heure de Paris).
const FONDATEUR_DEADLINE = new Date('2026-08-06T22:00:00Z').getTime()

export function FondateurFooter({ onCta }) {
  const [left, setLeft] = useState(() => FONDATEUR_DEADLINE - Date.now())
  useEffect(() => {
    try { studioEvents.tabClicked?.({ tab: 'fondateur_banner_shown', source_page: 'va_fiche' }) } catch { /* no-op */ }
    const id = setInterval(() => setLeft(FONDATEUR_DEADLINE - Date.now()), 1000)
    return () => clearInterval(id)
  }, [])
  if (left <= 0) return null
  const h = Math.floor(left / 3600000)
  const m = Math.floor((left % 3600000) / 60000)
  const s = Math.floor((left % 60000) / 1000)
  const pad = (n) => String(n).padStart(2, '0')
  return (
    <div className="rounded-2xl p-4 mt-4 text-white relative overflow-hidden"
      style={{ background: 'linear-gradient(120deg, #2a1657 0%, #4c1d95 55%, #6d28d9 100%)', boxShadow: '0 10px 26px rgba(76,29,149,0.35)' }}>
      <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full" style={{ background: 'rgba(254,130,4,0.25)', filter: 'blur(30px)' }} />
      <p className="text-[10.5px] font-extrabold uppercase tracking-widest" style={{ color: '#fdba74' }}>🏆 Offre fondateurs</p>
      <p className="text-[15px] font-extrabold leading-snug mt-1">Réservée aux 500 premiers comptes Merlin. Le tien en fait partie.</p>
      {/* Tarif façon paywall : prix barré + prix membre en gros */}
      <div className="flex items-baseline justify-center gap-2.5 mt-3">
        <span className="text-[17px] font-bold line-through" style={{ color: 'rgba(255,255,255,0.45)' }}>89 €</span>
        <span className="text-[30px] font-extrabold leading-none">29,90 €</span>
        <span className="text-[12.5px] font-bold" style={{ color: 'rgba(255,255,255,0.8)' }}>/mois</span>
      </div>
      <p className="text-center text-[11px] mt-0.5" style={{ color: 'rgba(255,255,255,0.7)' }}>
        en annuel · -45 % à vie · ou 49,90 €/mois en mensuel
      </p>
      <button type="button"
        onClick={() => {
          try { studioEvents.tabClicked?.({ tab: 'fondateur_footer_clicked', source_page: 'va_fiche' }) } catch { /* no-op */ }
          onCta?.()
        }}
        className="w-full mt-3 py-3.5 rounded-xl font-extrabold text-[14.5px] text-white"
        style={{ background: 'linear-gradient(135deg, #fe8204, #f59e0b)', boxShadow: '0 6px 18px rgba(254,130,4,0.4)' }}>
        Mes analyses illimitées pour 1 €/jour
      </button>
      {/* Décompte discret sous le CTA */}
      <p className="text-center text-[11px] mt-2 tabular-nums" style={{ color: 'rgba(255,255,255,0.7)' }}>
        ⏳ Il te reste {pad(h)}h {pad(m)}min {pad(s)}s
      </p>
    </div>
  )
}

export function VaGiftFiche({ analysisId, kind = 'free', onClose }) {
  const [meta, setMeta] = useState(null)
  useEffect(() => {
    try { studioEvents.vaFicheViewed({ analysis_id: analysisId, kind }) } catch {}
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])
  useEffect(() => {
    ;(async () => {
      try {
        const h = await authHeaders()
        const r = await fetch(`${API_URL}/api/studio/video-analysis/${analysisId}`, { headers: h })
        if (r.ok) setMeta(await r.json())
      } catch { /* le viewer plus bas réessaie de son côté */ }
    })()
  }, [analysisId])
  function openPaywall(trigger) {
    try { studioEvents.vaFicheAnalyzeAnother({ analysis_id: analysisId, kind, trigger }) } catch {}
    try { window.dispatchEvent(new CustomEvent('studio:open-analysis-paywall', { detail: { upgrade_to: 'pro_plus', code: 'va_fiche', va_kind: kind, va_handle: meta?.ig_username || null, trigger: 'va_fiche' } })) } catch {}
  }
  // CTA orange partagé (slot sous la note + bas de page). Arrivée campagne
  // fondateurs → même libellé que le bloc du bas : les deux CTA disent la
  // même chose et ouvrent le même paywall membre.
  const isFondateur = (() => { try { return localStorage.getItem('studio_member_offer') === '1' } catch { return false } })()
  const OrangeCTA = ({ where }) => (
    <button onClick={() => openPaywall(where)}
      className="block w-full text-center text-white font-extrabold text-[14.5px] rounded-xl py-3.5"
      style={{ background: 'linear-gradient(135deg, #fe8204, #f59e0b)', boxShadow: '0 8px 22px rgba(254,130,4,.35)' }}>
      {isFondateur ? 'Je profite de mes -45 % À VIE' : 'Analyser une autre de mes vidéos →'}
    </button>
  )
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" style={{ background: '#f4f2fa' }}>
      <div className="max-w-md mx-auto px-3.5 pt-4 pb-10">
        {/* Bandeau cadeau discret (plus de gros bloc violet : l'analyse a déjà
            sa vidéo + sa note en tête, on ne duplique pas la miniature). */}
        <div className="flex items-center justify-between gap-2 mb-3">
          <span className="text-[10.5px] font-extrabold px-2.5 py-1 rounded-full tracking-wide"
            style={{ background: 'rgba(254,130,4,.12)', border: '1px solid rgba(254,130,4,.45)', color: '#d97206' }}>
            🎁 ANALYSE OFFERTE · TOUT EST DÉBLOQUÉ
          </span>
          {onClose && (
            <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center text-ink-muted hover:bg-ink/5 shrink-0">×</button>
          )}
        </div>
        {/* L'ANALYSE COMPLÈTE — le même rapport que les abonnés, entier.
            Le CTA orange est injecté JUSTE SOUS LA NOTE (afterNoteSlot). */}
        <div className="rounded-2xl bg-white border border-border p-4">
          <VideoAnalysisViewer analysisId={analysisId} plan={null} onClose={onClose} hideHeader hideUpgrade
            afterNoteSlot={
              <div className="pt-1">
                <OrangeCTA where="after_note" />
                <p className="text-center text-[10.5px] text-ink-muted mt-1.5">
                  {isFondateur
                    ? 'Chaque vidéo analysée comme celle-ci, pour moins d\'un euro par jour en annuel.'
                    : 'Chaque vidéo que tu publies, analysée comme celle-ci. Automatiquement.'}
                </p>
              </div>
            } />
        </div>
        {/* CTA de fin : la demande vient de lui après avoir tout vu.
            Arrivée campagne fondateurs → le CTA -45% à vie remplace le générique. */}
        {isFondateur
          ? <FondateurFooter onCta={() => openPaywall('fondateur_footer')} />
          : <div className="mt-4"><OrangeCTA where="footer" /></div>}
        {onClose && (
          <button onClick={onClose} className="block w-full text-center text-[12px] text-ink-muted underline mt-3">
            Continuer vers mon espace
          </button>
        )}
      </div>
    </div>
  )
}

function AnalysisProgress({ data, steps, onCancel, hideCancel = false }) {
  const realPct = data.progress_pct || 0
  // Interpolation : si on stagne à 80 (étape Sonnet 60-90s), on fait
  // monter doucement le visuel jusqu'à 94% pour donner un sentiment
  // d'avancée continue. Reset dès que la vraie valeur dépasse le visuel.
  const [visualPct, setVisualPct] = useState(realPct)
  useEffect(() => {
    // Synchronise immédiatement si la vraie valeur dépasse le visuel
    if (realPct >= visualPct) setVisualPct(realPct)
  }, [realPct, visualPct])
  useEffect(() => {
    // Tick auto pendant les phases longues (entre 80 et 94)
    if (realPct < 80 || realPct >= 95) return
    const cap = 94
    const id = setInterval(() => {
      setVisualPct(prev => {
        if (prev >= cap) return prev
        // ralentit à mesure qu'on approche du cap
        const remaining = cap - prev
        const step = Math.max(0.15, remaining * 0.04)
        return Math.min(cap, prev + step)
      })
    }, 800)
    return () => clearInterval(id)
  }, [realPct])
  const pct = Math.round(Math.max(realPct, visualPct))

  // Sous-titre rotatif pendant l'étape Sonnet pour casser la sensation d'attente
  const SONNET_TIPS = [
    'Analyse en profondeur (60 à 90s)',
    'Évaluation du hook et de la rétention…',
    'Identification des leviers psychologiques…',
    'Mapping des seaux sémantiques…',
    'Rédaction du plan d\'action…',
  ]
  const [tipIdx, setTipIdx] = useState(0)
  useEffect(() => {
    if (realPct < 80 || realPct >= 95) return
    const id = setInterval(() => setTipIdx(i => (i + 1) % SONNET_TIPS.length), 5000)
    return () => clearInterval(id)
  }, [realPct])
  const displayStep = (realPct >= 80 && realPct < 95)
    ? SONNET_TIPS[tipIdx]
    : (data.current_step || 'En attente…')

  const merlinImg = pct > 70 ? merlinSucces : pct > 30 ? merlinIdee : pct > 0 ? merlinSort : merlinRecherche
  return (
    <div className="rounded-2xl bg-white border border-border p-6">
      <div className="flex items-center gap-4 mb-5">
        <img src={merlinImg} alt="Merlin" className="w-20 h-20 object-contain shrink-0"
          style={{ filter: 'drop-shadow(0 4px 12px rgba(var(--merlin-accent-rgb, 254,130,4),0.30))' }} />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider font-bold text-ink-muted mb-0.5">En cours</p>
          <p className="text-sm font-bold text-ink leading-tight mb-1 transition-opacity duration-500">{displayStep}</p>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-ink/5 rounded-full overflow-hidden">
              <div className="h-full transition-all duration-700 ease-out"
                style={{ width: `${pct}%`, background: 'linear-gradient(90deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))' }} />
            </div>
            <span className="text-xs font-bold text-orange shrink-0">{pct}%</span>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {steps.map((s, i) => {
          const done = pct >= s.pct
          const current = pct >= s.pct - 6 && pct < s.pct + 6 && !done
          return (
            <div key={i} className="flex items-center gap-2.5 text-xs transition-all duration-300"
              style={{ opacity: done ? 1 : current ? 1 : 0.55 }}>
              {done ? (
                <span className="w-5 h-5 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 text-white"
                  style={{ background: 'linear-gradient(135deg, #10b981, #059669)', boxShadow: '0 2px 6px rgba(16,185,129,0.30)' }}>
                  ✓
                </span>
              ) : current ? (
                <span className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                  style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.12)', border: '2px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.40)' }}>
                  <span className="w-2 h-2 rounded-full bg-orange animate-pulse" />
                </span>
              ) : (
                <span className="w-5 h-5 rounded-full flex items-center justify-center shrink-0 bg-ink/5 border border-ink/10">
                  <span className="w-1.5 h-1.5 rounded-full bg-ink/20" />
                </span>
              )}
              <span className={`leading-tight ${done ? 'text-green-700 font-bold' : current ? 'text-orange font-bold' : 'text-ink-faint'}`}>
                {s.label}
              </span>
            </div>
          )
        })}
      </div>

      {!hideCancel && (
        <button onClick={onCancel} className="mt-5 text-xs text-ink-muted hover:text-ink underline">
          Revenir aux reels (l'analyse continue en arrière-plan)
        </button>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// VIDEO ANALYSIS RESULT — structure analyse_json riche (style Merlin admin)
// 7 dimensions + plan d'action top 3 + 4 deep blocks (hook anatomy /
// seaux / leviers / commentaires) + script complet + description visuelle
// ═══════════════════════════════════════════════════════════════

const DIM_META = {
  hook:                    { label: 'Hook',        emoji: '🪝' },
  circuit_dopaminergique:  { label: 'Rythme',      emoji: '⚡' },
  boucles:                 { label: 'Boucles',     emoji: '🔄' },
  connexion:               { label: 'Connexion',   emoji: '🤝' },
  rehooks:                 { label: 'Rehooks',     emoji: '↻' },
  densite:                 { label: 'Densité',     emoji: '📊' },
  langage:                 { label: 'Langage',     emoji: '💬' },
}

function noteColor(n) {
  if (n >= 8) return '#10b981'
  if (n >= 6) return 'var(--merlin-accent-2, #f59e0b)'
  if (n >= 4) return 'var(--merlin-accent, #fe8204)'
  return '#dc2626'
}

function Stars({ score, max = 5, size = 11 }) {
  const stars = Math.round((score / 10) * max)
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: max }).map((_, i) => (
        <span key={i} style={{ fontSize: size, color: i < stars ? 'var(--merlin-accent, #fe8204)' : '#e5e0d4' }}>★</span>
      ))}
    </div>
  )
}

export function VideoAnalysisResult({ data, plan, onClose, hideHeader = false, onRelaunch, hideUpgrade = false, afterNoteSlot = null }) {
  const scoring = data.scoring || {}
  const a = scoring.analyse_json || {}
  const gemini = scoring.gemini || {}
  const meta = data.reel_metadata || {}
  const comments = scoring.comments
  // Plan effectif :
  // - Si plan prop = 'pro_plus' explicitement → on respecte (LP demo).
  // - Sinon auth.studioPlan (localStorage > user_metadata > DB) prime sur
  //   le prop backend qui renvoie 'free'/null tant que users.studio_plan
  //   n'existe pas.
  const auth = useAuth()
  const effectivePlan = plan === 'pro_plus' ? 'pro_plus' : (auth?.studioPlan || plan || null)
  const isProPlus = effectivePlan === 'pro_plus'
  const isPro = effectivePlan === 'pro' || effectivePlan === 'pro_plus'
  // Récompense checklist (23/07) : l'analyse offerte à la complétion des
  // étapes d'activation débloque le rapport COMPLET pour CETTE analyse.
  const isFree = !isPro && !data?.reward_full
  const [relaunching, setRelaunching] = useState(false)

  const scriptWordCount = gemini.script_complet
    ? gemini.script_complet.split(/\s+/).filter(Boolean).length
    : 0

  async function handleRelaunch() {
    if (relaunching) return
    if (!confirm('Relancer l\'analyse de cette vidéo ? L\'ancienne analyse sera remplacée.')) return
    setRelaunching(true)
    try { await onRelaunch?.() }
    finally { setRelaunching(false) }
  }

  return (
    <div className="space-y-4">
      {/* Header : reel info + bouton Relancer */}
      <div className="flex items-start gap-3">
        {!hideHeader && (
          <button onClick={onClose}
            className="w-9 h-9 rounded-full flex items-center justify-center text-ink-muted hover:text-ink hover:bg-ink/5 shrink-0">
            ←
          </button>
        )}
        {(meta.thumb_signed_url || meta.thumbnail_url) && (
          <img src={meta.thumb_signed_url || meta.thumbnail_url} alt="" className="w-14 h-20 object-cover rounded-md shrink-0" />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-wider font-bold text-ink-muted mb-0.5">Ta vidéo</p>
          <p className="text-sm font-bold text-ink leading-snug line-clamp-2">
            {meta.caption?.slice(0, 80) || data.reel_shortcode}
          </p>
          <p className="text-[10px] text-ink-faint mt-1">
            {meta.views?.toLocaleString('fr-FR')} vues · {meta.likes != null ? `${meta.likes.toLocaleString('fr-FR')} likes` : 'likes masqués'} · {meta.duration_seconds}s
          </p>
        </div>
        {onRelaunch && (
          <button onClick={handleRelaunch} disabled={relaunching}
            className="text-[11px] font-bold px-2.5 py-1.5 rounded-md transition-colors disabled:opacity-60 flex items-center gap-1 shrink-0"
            style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.10)', color: '#d97206', border: '1px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.25)' }}
            title="Relancer l'analyse">
            <span>↻</span>
            <span className="hidden sm:inline">{relaunching ? '…' : 'Relancer'}</span>
          </button>
        )}
      </div>

      {/* Bannière format détecté + note globale en gros à côté */}
      {(a.hook?.hook_type || a.note_globale != null) && (
        <div className="px-4 py-3 rounded-xl flex items-center justify-between gap-3"
          style={{ background: 'linear-gradient(135deg, var(--merlin-violet, #7c3aed), var(--merlin-violet-2, #5b21b6))' }}>
          <span className="text-white text-xs sm:text-sm font-bold leading-snug min-w-0 flex-1">
            {a.hook?.hook_type || 'Analyse vidéo'}
          </span>
          {a.note_globale != null && (
            <span className="flex items-baseline gap-0.5 font-bold shrink-0"
              style={{ color: '#fdba74', textShadow: '0 0 16px rgba(var(--merlin-accent-rgb, 254,130,4),0.55)' }}>
              <span className="text-3xl sm:text-4xl">{a.note_globale}</span>
              <span className="text-sm text-white/55">/10</span>
            </span>
          )}
        </div>
      )}

      {/* Signaux vs ta niche (Antonin 28/07) : réactions de la vidéo comparées à
          la médiane des vidéos virales de sa niche → montre le levier algo qui
          décroche. Données masquées = ratio null = chip absente (jamais un faux 0). */}
      {scoring.signaux_niche && (() => {
        const S = scoring.signaux_niche
        const rx = r => r == null ? null : (r < 0.1 ? '<0,1×' : r > 20 ? '20×+' : `${r.toFixed(1).replace('.', ',')}×`)
        const chips = [['❤️', 'likes', S.like], ['💬', 'commentaires', S.comment], ['🔁', 'partages', S.share], ['🔖', 'saves', S.save]].filter(([, , r]) => r != null)
        if (!chips.length) return null
        return (
          <div className="p-4 rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(254,130,4,.08), rgba(124,58,237,.07))', border: '1px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.25)' }}>
            <p className="text-[10px] font-bold uppercase tracking-wider text-ink-muted mb-2">📊 Tes réactions vs la moyenne de ta niche</p>
            <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${chips.length}, minmax(0, 1fr))` }}>
              {chips.map(([icon, label, r]) => {
                const tier = r >= 1.4 ? 'good' : r >= 0.7 ? 'mid' : 'low'
                const c = { good: ['rgba(14,159,110,.13)', 'rgba(14,159,110,.45)', '#0e9f6e'], mid: ['#f5f3fa', '#ece7f5', '#8b8496'], low: ['rgba(234,88,12,.09)', 'rgba(234,88,12,.28)', '#c2410c'] }[tier]
                return (
                  <div key={label} className="rounded-xl py-2 px-1 text-center" style={{ background: c[0], border: `1px solid ${c[1]}` }}>
                    <div className="text-[16px] leading-none">{icon}</div>
                    <div className="text-[15px] font-extrabold leading-none mt-1" style={{ color: c[2] }}>{rx(r)}</div>
                    <div className="text-[9px] font-semibold text-ink-muted mt-0.5">{label}</div>
                  </div>
                )
              })}
            </div>
            <p className="text-[10.5px] text-ink-muted mt-2 leading-snug"><b style={{ color: '#0e9f6e' }}>Vert</b> = tu sur-performes vs ta niche, <b style={{ color: '#c2410c' }}>orange</b> = tu sous-performes (le levier à travailler en priorité).</p>
          </div>
        )
      })()}

      {/* Slot injecté juste sous la note (fiche-cadeau : CTA « analyser une autre ») */}
      {afterNoteSlot}

      {/* Hook verbatim (audio ou visuel) */}
      {(gemini.hook_audio_verbatim || gemini.hook_visuel_verbatim) && (
        <div className="p-4 rounded-xl" style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),0.04)', border: '1px solid rgba(var(--merlin-violet-rgb, 124,58,237),0.15)' }}>
          <p className="text-[10px] font-bold uppercase tracking-wider text-purple-600 mb-1.5">Hook</p>
          <p className="text-sm italic leading-relaxed" style={{ color: 'var(--merlin-violet, #7c3aed)' }}>
            "{gemini.hook_audio_verbatim || gemini.hook_visuel_verbatim}"
          </p>
        </div>
      )}

      {/* 7 dimensions : chaque card est un <details> — note visible,
           commentaire dans le menu déroulant (fermé par défaut). */}
      <div>
        <p className="text-[10px] font-bold uppercase tracking-wider text-ink/40 mb-2.5">Analyse par dimension</p>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(DIM_META).map(([key, meta]) => {
            const dim = a[key]
            if (!dim?.note) return null
            return (
              <details key={key} className="group rounded-lg bg-white border border-border overflow-hidden">
                <summary className="cursor-pointer list-none flex items-start gap-2 p-3 hover:bg-ink/[0.02]">
                  <span className="text-base shrink-0 leading-none mt-0.5">{meta.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-ink">{meta.label}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <Stars score={dim.note} />
                      <span className="text-xs font-bold" style={{ color: noteColor(dim.note) }}>{dim.note}/10</span>
                    </div>
                  </div>
                  {dim.detail && (
                    <span className="text-ink-muted text-[10px] mt-1 shrink-0 transition-transform group-open:rotate-180">▾</span>
                  )}
                </summary>
                {dim.detail && (
                  <p className="text-[11px] text-ink-muted px-3 pb-3 leading-snug">{dim.detail}</p>
                )}
              </details>
            )
          })}
        </div>
      </div>

      {/* Forces — fermé par défaut */}
      {Array.isArray(a.forces) && a.forces.length > 0 && (
        <details className="group rounded-xl bg-white border border-border overflow-hidden">
          <summary className="px-4 py-3 cursor-pointer list-none flex items-center justify-between hover:bg-ink/[0.02]">
            <span className="text-sm font-bold text-green-700">✦ {a.forces.length} points forts</span>
            <span className="text-ink-muted text-xs transition-transform group-open:rotate-180">▾</span>
          </summary>
          <div className="px-4 pb-4 space-y-1.5">
            {a.forces.map((f, i) => (
              <p key={i} className="text-sm text-ink/75 leading-relaxed pl-3"
                style={{ borderLeft: '2px solid rgba(34,197,94,0.3)' }}>{f}</p>
            ))}
          </div>
        </details>
      )}

      {/* Faiblesses (avec conseils nestés) — fermé par défaut */}
      {Array.isArray(a.faiblesses) && a.faiblesses.length > 0 && (
        <details className="group rounded-xl bg-white border border-border overflow-hidden">
          <summary className="px-4 py-3 cursor-pointer list-none flex items-center justify-between hover:bg-ink/[0.02]">
            <span className="text-sm font-bold text-orange">→ {a.faiblesses.length} axes d'amélioration</span>
            <span className="text-ink-muted text-xs transition-transform group-open:rotate-180">▾</span>
          </summary>
          {/* Gratuit : au dépliage, TOUTE la catégorie est floutée avec un
              cadenas par-dessus (décision Antonin 22/07). Le contenu réel est
              rendu ouvert derrière le flou pour montrer la densité de ce qui
              attend dans l'essai. */}
          <div className="px-4 pb-4 relative">
            {isFree && (
              <div className="absolute inset-0 z-10 flex items-center justify-center px-6">
                <button type="button"
                  onClick={() => window.dispatchEvent(new CustomEvent('studio:open-analysis-paywall', { detail: { upgrade_to: 'studio_creator', from_free: true, code: 'free_no_analysis', trigger: 'axes_locked' } }))}
                  className="w-full max-w-xs px-4 py-3 rounded-xl font-bold text-[13px] text-white text-center transition-transform hover:scale-[1.02]"
                  style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', boxShadow: '0 8px 24px rgba(var(--merlin-accent-rgb, 254,130,4),0.40)' }}>
                  🔒 Tes {a.faiblesses.length} axes et leurs conseils<br />
                  <span className="font-normal text-[11.5px] text-white/90">{showsTrialUx() ? 'Débloquer avec mon essai gratuit →' : 'Débloquer mon accès →'}</span>
                </button>
              </div>
            )}
            <div className="space-y-2" style={isFree ? { filter: 'blur(6px)', pointerEvents: 'none', userSelect: 'none', opacity: 0.6 } : {}}
              aria-hidden={isFree ? 'true' : undefined}>
            {a.faiblesses.map((f, i) => {
              const isObj = typeof f === 'object' && f.label
              const label = isObj ? f.label : f
              const conseils = isObj ? (f.conseils || []) : []
              return (
                <details key={i} className="group" open={isFree || undefined}>
                  <summary className="text-sm font-bold text-ink/80 cursor-pointer list-none flex items-center gap-2 py-1">
                    <span className="text-orange text-xs transition-transform group-open:rotate-90">▶</span>
                    {label}
                  </summary>
                  {conseils.length > 0 && (
                    <div className="ml-4 mt-1 space-y-1">
                      {conseils.map((c, ci) => (
                        <p key={ci} className="text-xs text-ink-muted leading-relaxed pl-2.5"
                          style={{ borderLeft: '2px solid rgba(249,115,22,0.25)' }}>{c}</p>
                      ))}
                    </div>
                  )}
                </details>
              )
            })}
            </div>
          </div>
        </details>
      )}

      {/* ═══════ POINT DE BASCULE FREE → GATED ═══════
           Si free, tout ce qui suit (DeepBlocks, scripts, etc.) est rendu
           mais flouté + paywall flottant. */}
      {isFree && (
        <div className="relative" style={{ marginTop: '8px' }}>
          {/* Paywall flottant centré au-dessus du flou */}
          <div className="absolute inset-x-0 top-8 sm:top-12 z-20 flex justify-center px-4">
            <div className="max-w-md w-full p-5 rounded-2xl text-center"
              style={{
                background: 'linear-gradient(135deg, #1a1040, #2e1065)',
                boxShadow: '0 20px 50px rgba(46,16,101,0.45), 0 0 0 1px rgba(var(--merlin-accent-rgb, 254,130,4),0.30)',
              }}>
              <p className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: '#fdba74' }}>
                Inclus dans ton essai gratuit
              </p>
              <p className="text-sm text-white/90 mb-4 leading-relaxed">
                Ton essai gratuit de 7 jours débloque l'analyse complète : anatomie du hook, leviers psy et plan d'action.
              </p>
              <button onClick={() => window.dispatchEvent(new CustomEvent('studio:open-analysis-paywall', { detail: { upgrade_to: 'studio_creator', from_free: true, code: 'free_no_analysis' } }))}
                className="w-full py-3 rounded-xl font-bold text-sm text-white transition-all hover:scale-[1.02]"
                style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))', boxShadow: '0 8px 24px rgba(var(--merlin-accent-rgb, 254,130,4),0.40)' }}>
                Débloquer avec mon essai gratuit →
              </button>
              <p className="text-[10px] text-white/55 mt-2">7 jours gratuits · annulation en 1 clic · rappel avant facturation</p>
            </div>
          </div>
        </div>
      )}

      {/* Sections suivantes : floutées si free, normales sinon */}
      <div style={isFree ? { filter: 'blur(6px)', pointerEvents: 'none', userSelect: 'none', opacity: 0.65 } : {}}
        aria-hidden={isFree ? 'true' : undefined}>
      <div className="space-y-4">

      {/* Plan d'action top 3 (Pro only) — collapsible fermé par défaut */}
      {isProPlus && Array.isArray(a.plan_action_top3) && a.plan_action_top3.length > 0 && (
        <details className="group rounded-xl overflow-hidden"
          style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.06), rgba(var(--merlin-accent-rgb, 254,130,4),0.10))', border: '1px solid rgba(245,158,11,0.25)' }}>
          <summary className="px-4 py-3 cursor-pointer list-none flex items-center justify-between hover:bg-orange/[0.04]">
            <span className="text-xs font-bold uppercase tracking-wider text-orange-600">🎯 Plan d'action top 3</span>
            <span className="text-orange-600 text-xs transition-transform group-open:rotate-180">▾</span>
          </summary>
          <div className="px-4 pb-4 space-y-2.5">
            {a.plan_action_top3.slice(0, 3).map((p, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="text-sm font-bold text-orange-600 mt-0.5">{p.priorite || i + 1}.</span>
                <div className="flex-1">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded"
                      style={{ background: 'rgba(var(--merlin-violet-rgb, 124,58,237),0.12)', color: 'var(--merlin-violet, #7c3aed)' }}>
                      {p.bloc || '—'}
                    </span>
                    <span className="text-sm font-bold text-ink/90">{p.action}</span>
                  </div>
                  {p.pourquoi && (
                    <p className="text-xs text-ink-muted italic mt-1">{p.pourquoi}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* DeepBlock : Anatomie du hook */}
      <DeepBlock
        icon="🎣"
        title="Anatomie du hook"
        block={a.hook_anatomy}
        gated={!isProPlus}
        actuelRenderer={(act) => (
          <>
            {act?.formule_syntaxique && (
              <div><b className="text-ink/85">Formule :</b> <span className="italic">{act.formule_syntaxique}</span></div>
            )}
            {act?.mecanisme_rhetorique && (
              <div><b className="text-ink/85">Mécanisme :</b> {act.mecanisme_rhetorique}</div>
            )}
            {act?.emotion_primaire && (
              <div><b className="text-ink/85">Émotion :</b> {act.emotion_primaire}</div>
            )}
            {Array.isArray(act?.biais_cognitifs) && act.biais_cognitifs.length > 0 && (
              <div><b className="text-ink/85">Biais exploités :</b> {act.biais_cognitifs.join(', ')}</div>
            )}
            {act?.longueur_mots != null && (
              <div><b className="text-ink/85">Longueur :</b> {act.longueur_mots} mots</div>
            )}
          </>
        )}
      />

      {/* DeepBlock : Seaux sémantiques */}
      <DeepBlock
        icon="🪣"
        title="Seaux sémantiques"
        block={a.seaux_semantiques}
        gated={!isProPlus}
        actuelRenderer={(act) => (
          <>
            {act?.tribu_visee && <div><b className="text-ink/85">Tribu visée :</b> {act.tribu_visee}</div>}
            {act?.douleur_exploitee && <div><b className="text-ink/85">Douleur exploitée :</b> {act.douleur_exploitee}</div>}
            {act?.desir_active && <div><b className="text-ink/85">Désir activé :</b> {act.desir_active}</div>}
            {act?.ennemi_designe && <div><b className="text-ink/85">Ennemi désigné :</b> {act.ennemi_designe}</div>}
            {act?.emotion_dominante && <div><b className="text-ink/85">Émotion dominante :</b> {act.emotion_dominante}</div>}
            {act?.niveau_conscience && <div><b className="text-ink/85">Niveau de conscience :</b> {act.niveau_conscience}</div>}
          </>
        )}
      />

      {/* DeepBlock : Leviers psychologiques */}
      <DeepBlock
        icon="🧠"
        title="Leviers psychologiques"
        block={a.leviers_psychologiques}
        gated={!isProPlus}
        actuelRenderer={(act) => (
          <>
            {act?.raison_partager && <div><b className="text-ink/85">Raison de partager :</b> {act.raison_partager}</div>}
            {act?.raison_enregistrer && <div><b className="text-ink/85">Raison d'enregistrer :</b> {act.raison_enregistrer}</div>}
            {act?.capital_social && <div><b className="text-ink/85">Capital social :</b> {act.capital_social}</div>}
            {act?.mecanisme_viral && <div><b className="text-ink/85">Mécanisme viral :</b> <span className="italic">{act.mecanisme_viral}</span></div>}
            {/* Clamp : le LLM a déjà sorti 6/5 (fiche Antonin 22/07) */}
            {act?.potentiel_debat != null && <div><b className="text-ink/85">Potentiel de débat :</b> {Math.min(5, Number(act.potentiel_debat) || 0)}/5</div>}
          </>
        )}
      />

      {/* DeepBlock : Commentaires (si dispo) */}
      {a.commentaires_analyse && (
        <DeepBlock
          icon="💬"
          title={`Commentaires${comments?.stats?.total_fetched ? ` (${comments.stats.total_fetched})` : ''}`}
          block={a.commentaires_analyse}
          gated={!isProPlus}
          actuelRenderer={(act) => (
            <>
              {act?.sentiment_dominant && <div><b className="text-ink/85">Sentiment :</b> {act.sentiment_dominant}</div>}
              {Array.isArray(act?.verbatims_cles) && act.verbatims_cles.length > 0 && (
                <div className="mt-2">
                  <b className="text-ink/85">Verbatims clés :</b>
                  <ul className="mt-1 space-y-1">
                    {act.verbatims_cles.slice(0, 5).map((v, i) => (
                      <li key={i} className="text-xs italic text-ink-muted pl-3" style={{ borderLeft: '2px solid rgba(var(--merlin-violet-rgb, 124,58,237),0.25)' }}>"{v}"</li>
                    ))}
                  </ul>
                </div>
              )}
              {Array.isArray(act?.questions_audience) && act.questions_audience.length > 0 && (
                <div className="mt-2">
                  <b className="text-ink/85">Questions récurrentes :</b>
                  <ul className="mt-1 list-disc pl-5">
                    {act.questions_audience.slice(0, 3).map((q, i) => (
                      <li key={i} className="text-xs text-ink-muted">{q}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        />
      )}

      {/* Script complet collapsible */}
      {gemini.script_complet && (
        <details className="rounded-xl bg-white border border-border overflow-hidden">
          <summary className="px-4 py-3 cursor-pointer list-none flex items-center justify-between">
            <span className="text-sm font-bold text-ink/80">📝 Script complet ({scriptWordCount} mots)</span>
            <span className="text-ink-muted text-xs">▾</span>
          </summary>
          <div className="px-4 pb-4">
            <p className="text-xs text-ink-muted leading-relaxed whitespace-pre-wrap">{gemini.script_complet}</p>
          </div>
        </details>
      )}

      {/* Description visuelle retirée (Antonin 28/07) : elle fuite la sortie
          brute du backend et n'apporte rien à l'utilisateur. Elle reste utilisée
          en interne pour nourrir l'analyse Sonnet, mais n'est plus affichée. */}

      {/* Paywall créateur (en bas pour les non-Pro+) — pas pour les free
           qui ont déjà la modal flottante au-dessus du flou. hideUpgrade
           coupe court pour la démo LP (le visiteur n'est pas client). */}
      {!hideUpgrade && !isProPlus && !isFree && (
        <ProUpgradeBlock
          currentPlan={effectivePlan}
          label="Pour des analyses en illimité, passe en Pro. Le plan Créateur est limité à 1 analyse par semaine." />
      )}

      </div>
      </div>
    </div>
  )
}

// ── Bloc de deep analyse réutilisable (triptyque actuel / eval / score / améliorations) ──
function DeepBlock({ icon, title, block, gated, actuelRenderer }) {
  if (!block) return null
  const score = block.score_sur_10
  return (
    <details className="rounded-xl bg-white border border-border overflow-hidden">
      <summary className="px-4 py-3 cursor-pointer list-none flex items-center justify-between gap-3">
        <span className="text-sm font-bold text-ink/80 flex items-center gap-2">
          <span>{icon}</span>
          {title}
        </span>
        {typeof score === 'number' && (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
            style={{ background: `${noteColor(score)}1a`, color: noteColor(score) }}>
            {score}/10
          </span>
        )}
      </summary>
      <div className="px-4 pb-4 space-y-3">
        {/* État actuel */}
        {block.actuel && (
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-ink-muted mb-1.5">État actuel</p>
            <div className="text-xs text-ink space-y-1 leading-relaxed">
              {actuelRenderer ? actuelRenderer(block.actuel) : null}
            </div>
          </div>
        )}
        {/* Évaluation / Verdict */}
        {block.evaluation && (
          <div className="p-2.5 rounded-md" style={{ background: '#fff7ed', border: '1px solid #fed7aa' }}>
            <p className="text-[10px] uppercase tracking-wider font-bold text-orange-700 mb-1">Verdict</p>
            <p className="text-xs text-ink leading-relaxed">{block.evaluation}</p>
          </div>
        )}
        {/* Améliorations */}
        {Array.isArray(block.ameliorations) && block.ameliorations.length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-wider font-bold text-ink-muted mb-1.5">
              Améliorations prioritaires {gated && <span className="text-orange">(Pro)</span>}
            </p>
            {gated ? (
              <button onClick={() => window.dispatchEvent(new CustomEvent('studio:open-analysis-paywall', { detail: { upgrade_to: 'pro_plus' } }))}
                className="w-full text-left p-3 rounded-lg bg-orange/5 border border-orange/25 text-xs text-ink hover:bg-orange/10 transition-colors">
                🔒 Débloquer les {block.ameliorations.length} actions concrètes pour ce bloc
              </button>
            ) : (
              <div className="space-y-2">
                {block.ameliorations.map((am, i) => (
                  <div key={i} className="p-3 rounded-lg bg-ink/[0.02] border border-border">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider"
                        style={{ background: '#fee2e2', color: '#b91c1c' }}>
                        {am.quoi || '—'}
                      </span>
                      {am.impact_attendu && (
                        <span className="text-[10px] font-bold text-green-700">📈 {am.impact_attendu}</span>
                      )}
                    </div>
                    {am.probleme && (
                      <p className="text-xs text-ink/85 mb-1"><b>Problème :</b> {am.probleme}</p>
                    )}
                    {am.action && (
                      <p className="text-xs text-ink/85"><b>Action :</b> {am.action}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </details>
  )
}

function GlobalScoreCard({ score }) {
  const safeScore = typeof score === 'number' ? score : 0
  const verdict = safeScore >= 8 ? 'Excellent reel' : safeScore >= 6 ? 'Bon reel à affiner' : safeScore >= 4 ? 'Reel à retravailler' : 'Reel faible'
  return (
    <div className="relative overflow-hidden p-6 rounded-2xl text-center"
      style={{ background: 'radial-gradient(circle at 30% 20%, #2e1065 0%, #1a1040 50%, #0f0a1a 100%)' }}>
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(circle at 80% 80%, rgba(var(--merlin-accent-rgb, 254,130,4),0.18) 0%, transparent 60%)' }} />
      <div className="relative">
        <p className="text-[10px] uppercase tracking-wider font-bold mb-3" style={{ color: '#fdba74' }}>Score global</p>
        <div className="flex items-baseline justify-center gap-1 mb-2">
          <span className="text-7xl font-bold text-white leading-none">{safeScore.toFixed(1)}</span>
          <span className="text-2xl text-white/40">/10</span>
        </div>
        <p className="text-sm text-white/80">{verdict}</p>
      </div>
    </div>
  )
}

function HighlightCard({ kind, data }) {
  const isForce = kind === 'force'
  return (
    <div className="p-4 rounded-2xl"
      style={{
        background: isForce ? 'linear-gradient(135deg, #f0fdf4, white)' : 'linear-gradient(135deg, #fef2f2, white)',
        border: `1px solid ${isForce ? 'rgba(16,185,129,0.30)' : 'rgba(220,38,38,0.30)'}`,
      }}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-lg">{isForce ? '🏆' : '🔥'}</span>
        <p className="text-[10px] uppercase tracking-wider font-bold"
          style={{ color: isForce ? '#047857' : '#b91c1c' }}>
          {isForce ? 'Top force' : 'Top faiblesse'}
          {data.segment && (
            <span className="text-ink-muted ml-1">· {SEGMENT_LABELS[data.segment] || data.segment}</span>
          )}
        </p>
      </div>
      <p className="text-sm font-bold text-ink leading-snug mb-1.5">{data.label}</p>
      <p className="text-xs text-ink-muted leading-relaxed">{data.explanation}</p>
    </div>
  )
}

function SegmentCard({ segKey, label, description, seg, gated }) {
  if (!seg) return null
  const score = typeof seg.score === 'number' ? seg.score : 0
  const color = score >= 8 ? '#10b981' : score >= 6 ? 'var(--merlin-accent-2, #f59e0b)' : score >= 4 ? 'var(--merlin-accent, #fe8204)' : '#dc2626'
  return (
    <div className="rounded-xl bg-white border border-border overflow-hidden">
      <div className="flex items-center gap-3 p-3 border-b border-border">
        <div className="w-14 h-14 rounded-lg flex items-center justify-center text-white font-bold shrink-0 text-lg"
          style={{ background: `linear-gradient(135deg, ${color}, ${color}cc)` }}>
          {score.toFixed(1)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-ink leading-tight">{label}</p>
          <p className="text-[11px] text-ink-muted">{description}</p>
        </div>
      </div>
      <div className="p-3 space-y-2.5">
        {seg.force && (
          <div className="flex items-start gap-2">
            <span className="text-green-600 text-sm shrink-0 leading-none mt-0.5">✓</span>
            <p className="text-xs text-ink leading-relaxed flex-1"><b>Force.</b> {seg.force}</p>
          </div>
        )}
        {seg.faiblesse && (
          <div className="flex items-start gap-2">
            <span className="text-rose-600 text-sm shrink-0 leading-none mt-0.5">⚠</span>
            <p className="text-xs text-ink leading-relaxed flex-1"><b>À améliorer.</b> {seg.faiblesse}</p>
          </div>
        )}
        {gated ? (
          <div className="pt-2 mt-1 border-t border-border">
            <button onClick={() => window.dispatchEvent(new CustomEvent('studio:open-analysis-paywall', { detail: { upgrade_to: 'pro_plus' } }))}
              className="text-[11px] font-bold text-orange hover:underline flex items-center gap-1">
              🔒 Voir l'explication détaillée
            </button>
          </div>
        ) : seg.explication && (
          <div className="pt-2 mt-1 border-t border-border">
            <p className="text-[10px] uppercase tracking-wider font-bold text-ink-muted mb-1">Explication</p>
            <p className="text-xs text-ink-muted leading-relaxed">{seg.explication}</p>
          </div>
        )}
      </div>
    </div>
  )
}

function RecoCard({ reco, n }) {
  return (
    <div className="p-4 rounded-xl bg-white border border-border">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full shrink-0 flex items-center justify-center text-white font-bold text-sm"
          style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))' }}>
          {n}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-ink mb-1.5 leading-snug">{reco.title}</p>
          <p className="text-xs text-ink-muted leading-relaxed">{reco.detail}</p>
          {reco.expected_impact && (
            <p className="text-[10px] text-green-700 mt-2 font-bold flex items-center gap-1">
              <span>📈</span> {reco.expected_impact}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

function ProUpgradeBlock({ label, currentPlan }) {
  const isFree = !currentPlan || currentPlan === 'free'
  // L'analyse n'est jamais dans l'essai → free user passe direct au
  // plan Créateur (paid), créateur user upgrade vers Pro+.
  const targetPlan = isFree ? 'studio_creator' : 'pro_plus'
  const ctaLabel   = isFree ? 'Activer mon plan Créateur' : 'Passer en Pro · 89€/mo'
  const ctaSub     = isFree ? '39 €/mois. Annule en 1 clic.' : null
  const eyebrow    = isFree ? 'Studio Créateur' : 'Studio Pro'
  const eventCode  = isFree ? 'free_no_analysis' : 'weekly_quota_reached'

  return (
    <div className="p-5 rounded-2xl text-center"
      style={{ background: 'linear-gradient(135deg, #1a1040, #2e1065)' }}>
      <p className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: '#fdba74' }}>
        {eyebrow}
      </p>
      <p className="text-sm text-white/85 mb-4 leading-relaxed">{label}</p>
      <button onClick={() => window.dispatchEvent(new CustomEvent('studio:open-analysis-paywall', { detail: { upgrade_to: targetPlan, from_free: isFree, code: eventCode } }))}
        className="w-full py-3 rounded-xl font-bold text-sm text-white transition-all hover:scale-[1.01]"
        style={{
          background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))',
          boxShadow: '0 8px 24px rgba(var(--merlin-accent-rgb, 254,130,4),0.35)',
        }}>
        {ctaLabel}
      </button>
      {ctaSub && (
        <p className="text-[10px] text-white/55 mt-2">{ctaSub}</p>
      )}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// SECTION ACCOUNT — analyse globale Pro+
// ═══════════════════════════════════════════════════════════════
function AccountSection({ isProPlus, active }) {
  const [analyses, setAnalyses] = useState([])
  const [activeId, setActiveId] = useState(null)
  const [showConfirm, setShowConfirm] = useState(false)
  const [launching, setLaunching] = useState(false)
  const autoOpenedRef = useRef(false)

  async function loadList(force = false) {
    if (!force) {
      const cached = getCached('accountAnalyses')
      if (cached) {
        setAnalyses(cached)
        autoOpenLatest(cached)
        return
      }
    }
    try {
      const h = await authHeaders()
      const r = await fetch(`${API_URL}/api/studio/account-analysis`, { headers: h })
      const d = await r.json()
      if (r.ok) {
        const list = d.analyses || []
        setCached('accountAnalyses', list)
        setAnalyses(list)
        autoOpenLatest(list)
      }
    } catch { /* silent */ }
  }

  // Auto-open la dernière analyse done OU en cours (pas de switch entre
  // analyses utile vu qu'un seul compte). User lance → revient → voit
  // directement le résultat.
  function autoOpenLatest(list) {
    if (autoOpenedRef.current) return
    if (activeId) return
    const latest = list.find(a => a.status === 'done')
      || list.find(a => a.status === 'analyzing' || a.status === 'pending')
    if (latest) {
      autoOpenedRef.current = true
      setActiveId(latest.id)
    }
  }

  async function confirmLaunch() {
    setShowConfirm(false)
    setLaunching(true)
    try {
      const h = await authHeaders()
      const r = await fetch(`${API_URL}/api/studio/account-analysis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...h },
        body: JSON.stringify({}),
      })
      const d = await r.json()
      if (!r.ok) {
        if (d.code === 'pro_plus_required') {
          window.dispatchEvent(new CustomEvent('studio:open-analysis-paywall', { detail: { upgrade_to: 'pro_plus' } }))
          return
        }
        throw new Error(d.error || `HTTP ${r.status}`)
      }
      invalidateAnalysisCache('accountAnalyses')
      try { studioEvents.accountAnalysisStarted({ analysis_id: d.id }) } catch {}
      // Activation : une analyse COMPTE a demarre → coche l'etape 'account'.
      try { window.dispatchEvent(new CustomEvent('studio:account-analysis-started')) } catch {}
      setActiveId(d.id)
      loadList(true)
    } catch (e) {
      alert(`Erreur : ${e.message}`)
    } finally { setLaunching(false) }
  }

  useEffect(() => { loadList() }, [])

  // À CHAQUE entrée sur l'onglet "Mon compte" : on re-fetch frais et on rouvre
  // la dernière analyse done. Sinon, si l'onglet s'est monté AVANT que
  // l'analyse existe (liste vide cachée 30 min), on resterait bloqué sur
  // l'écran "Lancer le scan" alors qu'une analyse terminée existe en base.
  useEffect(() => {
    if (!active) return
    autoOpenedRef.current = false
    loadList(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active])

  // Custom event pour relancer une analyse depuis le viewer
  useEffect(() => {
    function handler(e) {
      const id = e.detail?.id
      if (id) setActiveId(id)
    }
    window.addEventListener('studio:open-account-analysis', handler)
    return () => window.removeEventListener('studio:open-account-analysis', handler)
  }, [])

  if (!isProPlus) {
    return (
      <div className="space-y-4">
        <div className="p-6 rounded-2xl text-center"
          style={{ background: 'linear-gradient(135deg, #1a1040, #2e1065)' }}>
          <img src={merlinIdee} alt="" className="w-20 h-20 mx-auto mb-4 object-contain"
            style={{ filter: 'drop-shadow(0 8px 24px rgba(var(--merlin-accent-rgb, 254,130,4),0.30))' }} />
          <div className="inline-flex items-center gap-1.5 mb-3 px-2.5 py-1 rounded-full"
            style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.15)', border: '1px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.35)' }}>
            <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#fdba74' }}>Studio Pro</span>
          </div>
          <h3 className="text-xl font-bold text-white leading-tight tracking-tight mb-3">
            Le scan complet de ton compte
          </h3>
          <p className="text-sm text-white/80 leading-relaxed mb-5 max-w-md mx-auto">
            Analyse ton profil, ta bio, et compare-toi aux top créateurs de ta niche. Applique la meilleure stratégie pour développer ton activité avec ton compte.
          </p>
          <button onClick={() => window.dispatchEvent(new CustomEvent('studio:open-analysis-paywall', { detail: { upgrade_to: 'pro_plus' } }))}
            className="px-6 py-3 rounded-xl font-bold text-sm text-white transition-all hover:scale-[1.01]"
            style={{
              background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))',
              boxShadow: '0 8px 24px rgba(var(--merlin-accent-rgb, 254,130,4),0.35)',
            }}>
            Passer en Pro · 89€/mo
          </button>
        </div>
      </div>
    )
  }

  if (activeId) {
    return <AccountAnalysisViewer analysisId={activeId} onClose={() => { setActiveId(null); loadList() }} />
  }

  return (
    <div className="space-y-4">
      <div className="p-5 rounded-2xl bg-white border border-border">
        <p className="text-[10px] uppercase tracking-wider font-bold text-ink-muted mb-1">Nouvelle analyse</p>
        <h3 className="text-base font-bold text-ink mb-2">Le scan complet de ton compte</h3>
        <p className="text-xs text-ink-muted leading-relaxed mb-4">
          Ta bio, ton business, et 3 créateurs à étudier dans ton style. Tout ce qu'il te faut pour savoir où t'améliorer.
        </p>
        <button onClick={() => setShowConfirm(true)} disabled={launching}
          className="w-full py-3 rounded-xl font-bold text-sm text-white transition-all hover:scale-[1.01] disabled:opacity-60"
          style={{
            background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))',
            boxShadow: '0 8px 24px rgba(var(--merlin-accent-rgb, 254,130,4),0.30)',
          }}>
          {launching ? 'On démarre…' : '🎯 Lancer le scan →'}
        </button>
      </div>

      {/* Historique retiré : 1 seul compte analysé, pas de switch utile */}

      {showConfirm && (
        <AccountAnalysisConfirmModal onCancel={() => setShowConfirm(false)} onConfirm={confirmLaunch} />
      )}
    </div>
  )
}

function AccountAnalysisConfirmModal({ onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onCancel}
      style={{ background: 'rgba(15,10,26,0.7)', backdropFilter: 'blur(6px)' }}>
      <div className="w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-5">
          <div className="text-center mb-4">
            <img src={merlinSort} alt="" className="w-20 h-20 mx-auto object-contain mb-3"
              style={{ filter: 'drop-shadow(0 4px 12px rgba(var(--merlin-accent-rgb, 254,130,4),0.25))' }} />
            <h2 className="text-xl font-bold text-ink leading-tight mb-1">On scanne ton compte ?</h2>
          </div>

          <div className="space-y-2 mb-5 p-3 rounded-xl" style={{ background: '#fffbf5', border: '1px solid #f1e9dc' }}>
            <div className="flex items-start gap-2 text-xs text-ink leading-relaxed">
              <span className="text-orange shrink-0">✓</span>
              <span>Tu sauras précisément quoi changer dans ta bio</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-ink leading-relaxed">
              <span className="text-orange shrink-0">✓</span>
              <span>Tu monétiseras ton audience avec les meilleures stratégies de ta niche</span>
            </div>
            <div className="flex items-start gap-2 text-xs text-ink leading-relaxed">
              <span className="text-orange shrink-0">✓</span>
              <span>Tu optimiseras ton profil pour transformer tes abonnés en clients</span>
            </div>
            <div className="flex items-center justify-end text-[11px] text-ink-muted pt-2 mt-1 border-t border-[#f1e9dc]">
              ⏱ 4 à 6 minutes
            </div>
          </div>

          <div className="flex gap-2">
            <button onClick={onCancel}
              className="flex-1 py-3 rounded-xl font-bold text-sm text-ink-muted bg-ink/5 hover:bg-ink/10 transition-colors">
              Pas maintenant
            </button>
            <button onClick={onConfirm}
              className="flex-1 py-3 rounded-xl font-bold text-sm text-white transition-all hover:scale-[1.02]"
              style={{
                background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))',
                boxShadow: '0 6px 20px rgba(var(--merlin-accent-rgb, 254,130,4),0.35)',
              }}>
              C'est parti →
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function AccountAnalysisViewer({ analysisId, onClose }) {
  const [data, setData] = useState(null)
  const pollRef = useRef(null)

  async function poll() {
    try {
      const h = await authHeaders()
      const r = await fetch(`${API_URL}/api/studio/account-analysis/${analysisId}`, { headers: h })
      const d = await r.json()
      if (r.ok) setData(d)
      if (d.status === 'done' || d.status === 'error') {
        if (pollRef.current) clearInterval(pollRef.current)
      }
    } catch { /* silent */ }
  }

  useEffect(() => {
    poll()
    pollRef.current = setInterval(poll, 3000)
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [analysisId])

  if (!data) return <LoadingState message="Chargement…" />

  if (data.status === 'error') {
    return (
      <div className="space-y-3">
        <button onClick={onClose} className="text-xs text-ink-muted hover:text-ink underline">← Retour</button>
        <div className="p-4 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm">
          <p className="font-bold">L'analyse a échoué.</p>
          <p className="text-xs mt-1">{data.error}</p>
        </div>
      </div>
    )
  }

  if (data.status !== 'done') {
    // "Revenir aux reels" : on NAVIGUE vers l'onglet "Pour toi" SANS fermer le
    // viewer. L'onglet Analyse reste monté (display:none) → l'analyse continue
    // vraiment à poller en fond. Au retour sur "Mon compte", le viewer affiche
    // l'avancement / le résultat sans relancer.
    return <AnalysisProgress data={data} steps={ACCOUNT_ANALYSIS_STEPS}
      onCancel={() => { try { window.dispatchEvent(new CustomEvent('studio:goto-home')) } catch {} }} />
  }

  async function relaunch() {
    try {
      const h = await authHeaders()
      const r = await fetch(`${API_URL}/api/studio/account-analysis`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...h },
        body: JSON.stringify({}),
      })
      const d = await r.json()
      if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`)
      invalidateAnalysisCache('accountAnalyses')
      // Close current then open new
      onClose?.()
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('studio:open-account-analysis', { detail: { id: d.id } }))
      }, 100)
    } catch (e) {
      alert(`Erreur : ${e.message}`)
    }
  }

  return <AccountAnalysisResult data={data} onClose={onClose} onRelaunch={relaunch} />
}

// ═══════════════════════════════════════════════════════════════
// ACCOUNT ANALYSIS RESULT — reuse des blocs MerlinAnalysisBlocks
// ═══════════════════════════════════════════════════════════════

// Avatar avec fallback en initiale si l'image Insta a expiré (CORS/403).
// Les analyses créées avant le mirror Supabase Storage ont des URLs stale.
function ProfileAvatar({ src, username }) {
  const [failed, setFailed] = useState(false)
  const initial = String(username || '?').replace(/^@/, '').charAt(0).toUpperCase() || '?'
  if (!src || failed) {
    return (
      <div className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-base border-2 border-white/20 shrink-0"
        style={{ background: 'linear-gradient(135deg, var(--merlin-violet, #7c3aed) 0%, var(--merlin-accent, #fe8204) 100%)', color: '#fff' }}>
        {initial}
      </div>
    )
  }
  return (
    <img src={src} alt="" onError={() => setFailed(true)}
      className="w-12 h-12 rounded-full object-cover border-2 border-white/20 shrink-0" />
  )
}

export function AccountAnalysisResult({ data, onClose, onRelaunch, publicMode = false }) {
  const ad = data.analysis_data || {}
  const profile = ad.profile || {}
  const avis = ad.merlin_avis || {}
  const bio = ad.bio_detailed
  const signals = ad.audience_signals?.signals
  const similar = ad.similar_creators || []
  const deep = ad.deep_analysis || {}
  const [relaunching, setRelaunching] = useState(false)

  async function handleRelaunch() {
    if (relaunching) return
    if (!confirm('Relancer une analyse complète de ton compte ? L\'ancienne reste accessible dans l\'historique.')) return
    setRelaunching(true)
    try { await onRelaunch?.() }
    finally { setRelaunching(false) }
  }

  return (
    <div className="space-y-4">
      {/* Action bar : relaunch + date (masquée en mode public) */}
      {!publicMode && (
        <div className="flex items-center justify-between gap-2 -mb-2">
          <p className="text-[10px] text-ink-faint">
            Analysé le {new Date(data.created_at).toLocaleDateString('fr-FR')}
            {data.duration_ms && ` · en ${Math.round(data.duration_ms / 1000)}s`}
          </p>
          <button onClick={handleRelaunch} disabled={relaunching}
            className="text-[11px] font-bold px-3 py-1.5 rounded-md transition-colors disabled:opacity-60 flex items-center gap-1.5"
            style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.10)', color: '#d97206', border: '1px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.25)' }}>
            <span>↻</span>
            {relaunching ? 'Démarrage…' : 'Relancer'}
          </button>
        </div>
      )}
      {/* Profile header */}
      <div className="rounded-2xl p-4 sm:p-5 text-white relative overflow-hidden"
        style={{ background: 'radial-gradient(circle at 30% 20%, #2e1065 0%, #1a1040 60%, #0f0a1a 100%)' }}>
        <div className="flex items-center gap-3 mb-2">
          <ProfileAvatar
            src={profile.profile_pic_url}
            username={profile.username || data.ig_username}
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold leading-tight truncate">@{profile.username || data.ig_username}</p>
            {profile.full_name && <p className="text-[11px] text-white/60 truncate">{profile.full_name}</p>}
          </div>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-white/80">
          {profile.follower_count != null && <span><b>{profile.follower_count.toLocaleString('fr-FR')}</b> abonnés</span>}
          {profile.media_count != null && <span><b>{profile.media_count}</b> posts</span>}
          {ad.aggregates?.vues_moyennes != null && <span><b>{ad.aggregates.vues_moyennes.toLocaleString('fr-FR')}</b> vues moy</span>}
          {ad.aggregates?.engagement_moyen != null && <span><b>{ad.aggregates.engagement_moyen}%</b> engagement</span>}
        </div>
      </div>

      {/* Le RADAR /100 (mêmes 6 axes que le score gratuit) remplace la note /10
          -> homogène avec la notation du scan. */}
      {ad.score && <AccountScore score={ad.score} username={profile.username || data.ig_username} hideCta />}

      {/* Direction stratégique en 1 ligne */}
      {avis.strategic_direction && (
        <div className="rounded-2xl p-4"
          style={{ background: 'linear-gradient(135deg, rgba(var(--merlin-accent-rgb, 254,130,4),0.06), rgba(var(--merlin-violet-rgb, 124,58,237),0.04))', border: '1px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.20)' }}>
          <p className="text-[10px] uppercase tracking-wider font-bold text-orange-700 mb-1">Direction stratégique</p>
          <p className="text-[12.5px] text-ink leading-snug">{avis.strategic_direction}</p>
        </div>
      )}

      {/* L'avis de Merlin — collapsible fermé par défaut */}
      <AvisDeMerlinBlock
        portrait={avis.portrait_ia}
        scoreGlobal={avis.note_globale}
        forces={avis.forces_recurrentes}
        faiblesses={avis.faiblesses_recurrentes}
        direction={avis.strategic_direction}
        icon={<img src={merlinIdee} alt="" className="w-5 h-5 object-contain" />}
        defaultOpen={false}
      />

      {/* Analyse de ta bio — collapsible fermé par défaut */}
      <BioAnalysisBlock
        bio={bio}
        profile={profile}
        competitors={bio?.competitor_bios}
        bioSugs={bio?.bio_suggestions}
      />

      {/* Tes inspirations (similar creators) */}
      {similar.length > 0 && (
        <details className="rounded-2xl bg-white border border-border overflow-hidden">
          <summary className="flex items-center justify-between gap-3 px-4 py-3 cursor-pointer list-none">
            <span className="text-sm font-bold text-ink flex items-center gap-2">
              <span>🎯</span>
              {similar.length} créateurs à étudier
            </span>
            <span className="text-ink-muted text-xs">▾</span>
          </summary>
          <div className="px-4 pb-4 space-y-2">
            {similar.slice(0, 5).map((c, i) => {
              const handle = c.instagram_handle || c.username || c.createur_nom
              const link = c.lien_profil || (c.instagram_handle ? `https://www.instagram.com/${c.instagram_handle}/` : null)
              return (
                <div key={i} className="p-3 rounded-lg border border-border">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    {link ? (
                      <a href={link} target="_blank" rel="noreferrer"
                        className="text-sm font-bold text-ink hover:text-orange transition-colors">
                        @{handle}
                      </a>
                    ) : (
                      <p className="text-sm font-bold text-ink">@{handle}</p>
                    )}
                    <div className="flex items-center gap-2 text-[10px] text-ink-muted shrink-0">
                      {c.followers != null && c.followers > 0 && (
                        <span>{c.followers.toLocaleString('fr-FR')} abos</span>
                      )}
                      {c.total_vues != null && c.total_vues > 0 && (
                        <span>{c.total_vues.toLocaleString('fr-FR')} vues</span>
                      )}
                    </div>
                  </div>
                  {c.avg_engagement != null && c.avg_engagement > 0 && (
                    <p className="text-[10px] text-ink-muted">Engagement moyen : {c.avg_engagement}%</p>
                  )}
                  {c.createur_nom && c.createur_nom !== handle && (
                    <p className="text-[10px] text-ink-faint mt-0.5">{c.createur_nom}</p>
                  )}
                </div>
              )
            })}
          </div>
        </details>
      )}

      {/* Signaux d'audience — bloc partagé (re-import from MerlinAnalysisBlocks) */}
      <AudienceSignalsBlock signals={signals} />

      {/* Recommandations — fermé par défaut (sera ouvert par le user si voulu) */}
      {Array.isArray(ad.recommandations?.recos) && ad.recommandations.recos.length > 0 && (
        <details className="group rounded-2xl bg-white border border-border overflow-hidden">
          <summary className="flex items-center justify-between gap-3 px-4 py-3 cursor-pointer list-none">
            <span className="text-sm font-bold text-ink flex items-center gap-2">
              <span>🎬</span>
              Plan d'actions Merlin · {ad.recommandations.recos.length}
            </span>
            <span className="text-ink-muted text-xs transition-transform group-open:rotate-180">▾</span>
          </summary>
          <div className="px-4 pb-4 space-y-2">
            {ad.recommandations.recos.map((r, i) => (
              <div key={i} className="p-3 rounded-lg" style={{ background: 'rgba(var(--merlin-accent-rgb, 254,130,4),0.04)', border: '1px solid rgba(var(--merlin-accent-rgb, 254,130,4),0.15)' }}>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, var(--merlin-accent, #fe8204), var(--merlin-accent-2, #f59e0b))' }}>
                    {r.priorite || i + 1}
                  </span>
                  <p className="text-sm font-bold text-ink">{r.titre}</p>
                </div>
                {r.action && <p className="text-xs text-ink/80 leading-relaxed mb-1"><b>Action :</b> {r.action}</p>}
                {r.impact_attendu && <p className="text-[10px] text-green-700 font-bold">📈 {r.impact_attendu}</p>}
                {r.delai && <p className="text-[10px] text-ink-muted mt-1">⏱ {r.delai}</p>}
              </div>
            ))}
            {ad.recommandations.synthese && (
              <div className="mt-3 p-3 rounded-lg bg-ink/[0.03]">
                <p className="text-xs text-ink leading-relaxed italic">{ad.recommandations.synthese}</p>
              </div>
            )}
          </div>
        </details>
      )}
    </div>
  )
}

// AudienceSignalsBlock importé depuis MerlinAnalysisBlocks.jsx

function LoadingState({ message }) {
  return (
    <div className="flex items-center justify-center gap-3 py-10 text-sm text-ink-muted">
      <span className="w-4 h-4 border-2 border-orange/30 border-t-orange rounded-full animate-spin" />
      <span>{message}</span>
    </div>
  )
}


// ── DÉMO DU TUTO — une VRAIE analyse vidéo (fixture réelle d'un créateur)
// affichée pendant le tour guidé pour montrer la valeur, même si elle ne
// vient pas du compte de l'utilisateur. Pilotée par events :
//   studio:tour-show-analysis-demo / studio:tour-hide-analysis-demo
// z-[300] : au-dessus du FormatDetailModal (200), SOUS le spotlight (1200)
// pour que le trou lumineux puisse la mettre en avant.
export function TourVideoAnalysisDemo() {
  const [show, setShow] = useState(false)
  useEffect(() => {
    const on = () => setShow(true)
    const off = () => setShow(false)
    window.addEventListener('studio:tour-show-analysis-demo', on)
    window.addEventListener('studio:tour-hide-analysis-demo', off)
    return () => {
      window.removeEventListener('studio:tour-show-analysis-demo', on)
      window.removeEventListener('studio:tour-hide-analysis-demo', off)
    }
  }, [])
  if (!show) return null
  // z-index 10000 : AU-DESSUS du voile du tuto (9999) et SOUS sa bulle
  // (10001) → l'utilisateur peut faire défiler l'exemple et dérouler les
  // sections PENDANT le tour, pour percevoir la valeur par lui-même.
  return (
    <div className="fixed inset-0 flex items-start justify-center px-3 pt-3 sm:pt-6"
      style={{ zIndex: 10000, pointerEvents: 'none' }}>
      <div data-tour="analysis-demo" className="w-full sm:max-w-sm bg-bg rounded-2xl shadow-2xl overflow-y-auto"
        style={{ maxHeight: '56vh', pointerEvents: 'auto', boxShadow: '0 12px 50px rgba(8,6,24,0.65), 0 0 0 1px rgba(167,139,250,0.25)' }}>
        <div className="sticky top-0 z-10 px-4 py-2.5 text-center"
          style={{ background: 'linear-gradient(135deg, var(--merlin-violet, #7c3aed), var(--merlin-violet-soft, #9f67ff))' }}>
          <p className="text-[11px] font-extrabold text-white uppercase tracking-wider">Exemple réel : la vidéo d'un créateur, analysée par Merlin</p>
          <p className="text-[10px] text-white/85 mt-0.5">Fais défiler et ouvre les sections 👇</p>
        </div>
        <div className="p-3">
          <VideoAnalysisResult data={ANTONIN_VIDEO_ANALYSIS} plan="pro_plus" hideHeader hideUpgrade />
        </div>
      </div>
    </div>
  )
}
